'use client';

import { Box, freddyTheme, Typography } from '@coolblue-development/freddy';
import { useStreamHandler } from '@coolblue-development/next-chat/client';
import type { PostMessageResponse } from '@coolblue-development/next-chat/types';
import { AdviceCue, AdviceCueState } from '@coolblue-development/next-chatbot-ui/client';
import type { Product } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { type ReactNode, useRef } from 'react';

type Props = {
  accountName?: string;
  correlationId: string;
  endpointUrl: string;
  product: Product;
};

const AdviceCueWrapper = ({
  accountName,
  correlationId,
  endpointUrl,
  product
}: Props): ReactNode => {
  const { t } = useTranslation('webshop.productPage');

  const boxRef = useRef<HTMLDivElement>(null);

  const { state, sendUserMessage } = useStreamHandler<AdviceCueState>({
    conversationEndpointUrl: endpointUrl,
    initialState: {
      uiMessages: [],
      showError: false,
      followUpQuestions: product.adviceCueAdvice?.questions ?? []
    },
    correlationId,
    customMessageHandlers: {
      AdviceCueFollowupQuestions: (
        incomingMessage: PostMessageResponse,
        currentState: AdviceCueState
      ) => {
        if (incomingMessage.type === 'AdviceCueFollowupQuestions') {
          return {
            ...currentState,
            followUpQuestions: incomingMessage?.payload?.questions || []
          };
        }
        return currentState;
      }
    }
  });

  const adjustScrollPosition = () => {
    boxRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      ref={boxRef}
      sx={{
        position: 'sticky',
        top: `calc(var(--cb-header-height) + ${freddyTheme.spacing[1000]})`,
        scrollMarginTop: freddyTheme.spacing[1000],
        [freddyTheme.breakpoint.minWidth('md')]: {
          scrollMarginTop: `calc(var(--cb-header-height) + ${freddyTheme.spacing[1000]})`
        }
      }}
    >
      <AdviceCue
        title={
          accountName
            ? t('adviceCues.title.withName', {
                customerName: accountName,
                productType: product.productType.singularName
              })
            : t('adviceCues.title.withoutName', {
                productType: product.productType.singularName
              })
        }
        state={state}
        onSendMessage={message => {
          sendUserMessage(message, {
            chatSource: 'product_advice_cue_agent'
          });
          adjustScrollPosition();
        }}
        productTypeName={product.productType.singularName}
      />
      <Typography variant='caption' mt={200}>
        This feature is currently available only for internal users.
      </Typography>
    </Box>
  );
};

export default AdviceCueWrapper;
